import {
  createUseFluxContext,
  createUseSelector,
  createUseSelectorSync,
} from '@packages/systems/flux';
import type {DesignerStores} from './DesignerStores';
import type {DesignerFlux} from './FluxTypes';
// eslint-disable-next-line webflow/no-dangerous-imports
import useIsMounted from '@packages/systems/ui/hooks/useIsMounted';
import {useCallback} from 'react';
/**
 * ## Example usage
 *
 * ```js
 * import {shallowEqual} from 'react-recompose';
 * import {createSelector} from 'reselect-legacy';
 * import {useDesignerSelector} from '@packages/systems/designer/flux';
 *
 * const selectDesignerStoreProps = createSelector(
 *   (state: DesignerStores) => getLiveComponentMap(state.DesignerStore),
 *   (state: DesignerStores) => getIsPreviewWorkflow(state.DesignerStore),
 *   (components, isPreviewMode) => ({
 *     components,
 *     isPreviewMode,
 *   })
 * );
 *
 * const MyComponent = (props) => {
 *   // Using a memoized selector _outside_ the render function body
 *   const designerStoreProps = useDesignerSelector(selectDesignerStoreProps);

 *   // Using a Selector that includes own props in the render _must_ be memoized
 *   const {elementId} = props;
 *   const selectElementFromCurrentComponent = React.useCallback(state => {
 *     const currentComponent = selectCurrentComponent(state);
 *     const currentRender = Component.getRender(currentComponent);
 *     const selectedElement = Expressions.getElementById(currentRender, elementId);
 *     return selectedElement ? selectedElement : null;
 *   }, [elementId]);
 *
 *   const someOtherData = useDesignerSelector(selectElementFromCurrentComponent)
 * ...
 * };
 * ```
 *
 * You can also pass in an optional equality comparison function in the second argument:
 *
 * ```js
 * // default
 * useDesignerSelector(someSelector, (a, b) => a === b);
 *
 * // custom comparison
 * useDesignerSelector(someSelector, (a, b) => shallowEqual(a, b))
 * ```
 *
 * By default, `useDesignerSelector` uses strict `===` reference equality checks, where `a` is the
 * previous selected state and `b` is the next selected state. If the result are the same,
 * then it does not trigger a rerender.
 *
 * For best practices, see this Performance Best Practices document.
 * @link https://webflow.atlassian.net/wiki/spaces/ENG/pages/627540013/Performance+Best+Practices#createSelector
 */
export const useDesignerSelector = createUseSelector<DesignerStores>();
export const useDesignerSelectorSync = createUseSelectorSync<DesignerStores>();

const useFluxContextAny = createUseFluxContext<any, any, any>();

/**
 * ## Example usage
 *
 * ```js
 * import {useEffect} from 'react';
 * import {useFluxContext} from '@packages/systems/designer/flux';
 *
 * const MyComponent = () => {
 *   const flux = useFluxContext();
 *
 *   useEffect(() => {
 *     flux.onChange(doSomething);
 *   }, [])
 * ...
 * };
 * ```
 */
export const useFluxContext = <T extends DesignerFlux = DesignerFlux>() => {
  const flux = useFluxContextAny();

  if (flux) {
    return flux as T;
  }

  throw new Error(
    'Could not find any Flux context. Ensure your app wrapped with either <FluxContext.Provider>.'
  );
};

/**
 * ## Example usage
 *
 * ```js
 * import {useCallback} from 'react';
 * import {useDispatch, useSafeDispatch} from '@packages/systems/designer/flux';
 *
 * const MyComponent = () => {
 *   const dispatch = useDispatch();
 *   const safeDispatch = useSafeDispatch();
 *
 *   const toggleTheThing = useCallback(() => {
 *     dispatch({type: 'THING_TOGGLED'});
 *   }, [dispatch])
 *
 *   const sendData = useCallback((nextData) => {
 *     dispatch({type: 'DATA_SENT', payload: nextData});
 *   }, [dispatch])
 *
 *   const requestSomeStuff = async () => {
 *     const result = await fetch(...);
 *     safeDispatch({type: 'RECEIVED', payload: result);
 *   }
 * ...
 * };
 * ```
 */
export const useDispatch = () => useFluxContext().dispatch;

export const useSafeDispatch = () => {
  const mountedRef = useIsMounted();
  const dispatch = useDispatch();
  const safeDispatch: typeof dispatch = (action) =>
    mountedRef.current ? dispatch(action) : undefined;

  // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  return useCallback(safeDispatch, [dispatch]);
};
